
import { useCallback, useRef, useState } from "react";
import { TreemapItem } from "../../meta";

interface Rect {
    item: TreemapItem;
    x: number;
    y: number;
    w: number;
    h: number;
}

/**Layout squarified: o maior ocupa um lado inteiro, o restante divide o outro lado recursivamente */
function squarify(items: TreemapItem[], x: number, y: number, w: number, h: number): Rect[] {
    if (items.length === 0) return [];
    if (items.length === 1) return [{ item: items[0], x, y, w, h }];

    const total = items.reduce((s, i) => s + i.value, 0);
    const first = items[0];
    const ratio = first.value / total;

    const rects: Rect[] = [];

    if (w >= h) {
        const leftW = w * ratio;
        rects.push({ item: first, x, y, w: leftW, h });
        rects.push(...squarify(items.slice(1), x + leftW, y, w - leftW, h));
    } else {
        const topH = h * ratio;
        rects.push({ item: first, x, y, w, h: topH });
        rects.push(...squarify(items.slice(1), x, y + topH, w, h - topH));
    }

    return rects;
}

interface TreemapChartProps {
    data: TreemapItem[];
    width?: number | string;
    is_empty?: boolean;
}

const SVG_W = 270;
const SVG_H = 160;
const GAP = 3;

export const TreemapChart: React.FC<TreemapChartProps> = ({ data, width, is_empty }) => {

    const rects = squarify(data, 0, 0, SVG_W, SVG_H);

    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const tooltipRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleMouseMove = useCallback((e: React.MouseEvent, i: number) => {
        const container = containerRef.current;
        if (!container) return;

        const bounds = container.getBoundingClientRect();
        const tooltip = tooltipRef.current;
        const tooltipW = tooltip?.offsetWidth ?? 150;
        const tooltipH = tooltip?.offsetHeight ?? 80;
        const offset = 15;

        let posX = e.clientX - bounds.left + offset;
        let posY = e.clientY - bounds.top + offset;

        // Flip horizontally if overflows right edge of viewport
        if (e.clientX + tooltipW + offset > window.innerWidth) {
            posX = e.clientX - bounds.left - tooltipW - offset;
        }

        // Flip vertically if overflows bottom edge of viewport
        if (e.clientY + tooltipH + offset > window.innerHeight) {
            posY = e.clientY - bounds.top - tooltipH - offset;
        }

        setHoveredIndex(i);
        setTooltipPos({ x: posX, y: posY });
    }, []);

    if (is_empty) {
        return (
            <div style={{ position: 'relative' }}>
                <svg
                    width={width || '100%'}
                    viewBox={`-${GAP} -${GAP} ${SVG_W + GAP * 2} ${SVG_H + GAP * 2}`}
                >
                    <rect
                        x={0}
                        y={0}
                        width={SVG_W}
                        height={SVG_H}
                        fill="#f5f5f5"
                        rx={8}
                    />
                    <text
                        x={SVG_W / 2}
                        y={SVG_H / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="#bbb"
                        fontSize={14}
                        fontWeight={500}
                        fontFamily="'Segoe UI', sans-serif"
                    >
                        Sem dados
                    </text>
                </svg>
            </div>
        );
    }

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <svg
                width={width || '100%'}
                viewBox={`-${GAP} -${GAP} ${SVG_W + GAP * 2} ${SVG_H + GAP * 2}`}
            >
                {/* Gradiente para o primeiro quadrado */}
                <defs>
                    <linearGradient id="grad-treemap-0" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="rgba(93, 165, 125, 1)" />
                        <stop offset="55%" stopColor="rgba(126, 189, 97, 1)" />
                        <stop offset="100%" stopColor="rgba(126, 189, 97, 1)" />
                    </linearGradient>
                </defs>

                {/* Fundo */}
                <rect
                    x={-GAP}
                    y={-GAP}
                    width={SVG_W + GAP * 2}
                    height={SVG_H + GAP * 2}
                    fill="#fff"
                    rx={8}
                />

                {rects.map((r, i) => {
                    const pct = Math.round(r.item.value);
                    const innerW = Math.max(0, r.w - GAP);
                    const innerH = Math.max(0, r.h - GAP);
                    const innerX = r.x + GAP / 2;
                    const innerY = r.y + GAP / 2;

                    const titleSize = Math.max(11, Math.min(20, innerW / 8, innerH / 4));
                    const detailSize = Math.max(9, titleSize - 3);
                    const padding = 6;

                    const canFitLabel = innerW > 30 && innerH > 20;
                    const canFitDetail = innerH > 38 && !!r.item.detail;

                    const truncate = (text: string, fSize: number) => {
                        const maxChars = Math.floor((innerW - padding * 2) / (fSize * 0.55));
                        if (text.length <= maxChars) return text;
                        return text.slice(0, Math.max(1, maxChars - 1)) + '…';
                    };

                    const clipId = `clip-treemap-${i}`;

                    const isHovered = hoveredIndex === i;
                    const isAnyHovered = hoveredIndex !== null;
                    const centerX = innerX + innerW / 2;
                    const centerY = innerY + innerH / 2;
                    const scale = isHovered ? 1.02 : 1;

                    const geo = 'x 0.5s ease, y 0.5s ease, width 0.5s ease, height 0.5s ease, font-size 0.5s ease';

                    return (
                        <g
                            key={r.item.label + i + 'treemap'}
                            style={{
                                animation: `treemapEntry 0.5s ease-out ${i * 0.12}s both`,
                                transformOrigin: `${centerX}px ${centerY}px`,
                            }}
                        >
                            <g
                                style={{
                                    opacity: isAnyHovered && !isHovered ? 0.4 : 1,
                                    transition: 'opacity 0.2s ease, transform 0.2s ease',
                                    transformOrigin: `${centerX}px ${centerY}px`,
                                    transform: `scale(${scale})`,
                                }}
                            >
                                <defs>
                                    <clipPath id={clipId}>
                                        <rect x={innerX} y={innerY} width={innerW} height={innerH} rx={5} style={{ transition: geo }} />
                                    </clipPath>
                                </defs>

                                <rect
                                    x={innerX}
                                    y={innerY}
                                    width={innerW}
                                    height={innerH}
                                    fill={i === 0 ? 'url(#grad-treemap-0)' : r.item.color}
                                    rx={5}
                                    style={{ cursor: 'pointer', transition: `${geo}, fill 0.5s ease` }}
                                    onMouseMove={(e) => {
                                        handleMouseMove(e, i);
                                    }}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                />

                                {canFitLabel && (
                                    <g clipPath={`url(#${clipId})`} style={{ pointerEvents: 'none' }}>
                                        {/* Nome */}
                                        <text
                                            x={r.x + r.w / 2}
                                            y={r.y + r.h / 2 - (canFitDetail ? titleSize * 1.1 : titleSize * 0.4)}
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            fill={r.item.textColor || '#fff'}
                                            fontSize={titleSize}
                                            fontWeight={600}
                                            fontFamily="'Segoe UI', sans-serif"
                                            style={{ transition: geo }}
                                        >
                                            {truncate(r.item.label, titleSize)}
                                        </text>
                                        {/* Porcentagem */}
                                        <text
                                            x={r.x + r.w / 2}
                                            y={r.y + r.h / 2 + (canFitDetail ? 0 : titleSize * 0.6)}
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            fill={r.item.textColor || '#fff'}
                                            fontSize={titleSize + 2}
                                            fontWeight={800}
                                            fontFamily="'Segoe UI', sans-serif"
                                            style={{ transition: geo }}
                                        >
                                            {pct}%
                                        </text>
                                        {/* Área */}
                                        {canFitDetail && r.item.detail && (
                                            <text
                                                x={r.x + r.w / 2}
                                                y={r.y + r.h / 2 + titleSize * 1.1}
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                                fill={r.item.textColor ? `${r.item.textColor}B3` : 'rgba(255,255,255,0.7)'}
                                                fontSize={detailSize}
                                                fontFamily="'Segoe UI', sans-serif"
                                                style={{ transition: geo }}
                                            >
                                                {truncate(r.item.detail, detailSize)}
                                            </text>
                                        )}
                                    </g>
                                )}
                            </g>
                        </g>
                    );
                })}
            </svg>

            {hoveredIndex !== null && (
                <div
                    ref={tooltipRef}
                    style={{
                        position: 'absolute',
                        left: tooltipPos.x,
                        top: tooltipPos.y,
                        background: 'rgba(0, 0, 0, 0.8)',
                        padding: 7,
                        color: '#fff',
                        fontWeight: 500,
                        borderRadius: 5,
                        fontSize: 13,
                        zIndex: 200,
                        pointerEvents: 'none',
                    }}
                >
                    <div style={{ margin: 0, whiteSpace: 'nowrap', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontWeight: 700 }}>
                            {data[hoveredIndex].label}
                        </span>
                        <span>Porcentagem: {Math.round(data[hoveredIndex].value)}%</span>
                        {data[hoveredIndex].detail && (
                            <span>Área: {data[hoveredIndex].detail}</span>
                        )}
                        {data[hoveredIndex].sementes && (
                            <span>Sementes: {data[hoveredIndex].sementes}</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
